import * as Sentry from '@sentry/react'

const IGNORED_HTTP_STATUSES = [400, 401, 403, 404, 409, 422]

function getHttpStatus(error: unknown): number | undefined {
  if (error && typeof error === 'object' && 'response' in error) {
    const response = (error as { response?: { status?: number } }).response
    return response?.status
  }
  return undefined
}

function isCancelOrAbort(error: unknown): boolean {
  if (error && typeof error === 'object' && 'code' in error) {
    const code = (error as { code?: string }).code
    if (code === 'ERR_CANCELED' || code === 'ABORT_ERR') return true
  }
  if (error instanceof Error && error.name === 'AbortError') return true
  return false
}

function beforeSend(
  event: Sentry.ErrorEvent,
  hint: Sentry.EventHint
): Sentry.ErrorEvent | null {
  const error = hint.originalException
  if (isCancelOrAbort(error)) return null
  const status = getHttpStatus(error)
  if (status !== undefined && IGNORED_HTTP_STATUSES.includes(status))
    return null
  return event
}

export function initSentry(): void {
  const dsn = process.env.REACT_APP_SENTRY_DSN
  if (!dsn) return

  Sentry.init({
    dsn,
    environment: process.env.REACT_APP_SENTRY_ENV || process.env.NODE_ENV,
    release: process.env.REACT_APP_SENTRY_RELEASE,
    beforeSend,
    tracesSampleRate: 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
  })

  if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
    const win = window as Window & {
      Sentry?: typeof Sentry
      sendSentryTest?: () => void
    }
    win.Sentry = Sentry
    win.sendSentryTest = () => {
      Sentry.captureException(new Error('Sentry test from doctor app'))
      console.info(
        'Sentry test event sent. Check your project at sentry.io → Issues.'
      )
    }
  }
}
